import { Directive, ElementRef, Renderer2, Input, OnInit } from '@angular/core';

@Directive({
  selector: '[ncMagicCard]'
})
export class MagicCardDirective implements OnInit {
  /** 渐变大小，单位px */
  @Input() ncGradientSize: number = 200;
  /** 内部渐变颜色，可以是单色或颜色数组 */
  @Input() ncGradientColor: string | string[] = '#D9D9D955';
  /** 内部渐变透明度 */
  @Input() ncGradientOpacity: number = 0.8;
  /** 边框颜色，可以是单色或颜色数组 */
  @Input() ncBorderColor: string | string[] = ["#9E7AFF", "#FE8BBB"];
  /** 卡片背景颜色 */
  @Input() ncCardBackground: string = 'white';

  private mouseX: number = -200;
  private mouseY: number = -200;
  private borderElement!: HTMLElement;
  private backgroundElement!: HTMLElement;
  private gradientElement!: HTMLElement;

  private handleMouseEnter = () => {
    this.renderer.setStyle(this.borderElement, 'opacity', '1');
    this.renderer.setStyle(this.gradientElement, 'opacity', this.ncGradientOpacity.toString());
  };

  private handleMouseMove = (e: MouseEvent) => {
    const rect = this.el.nativeElement.getBoundingClientRect();
    this.mouseX = e.clientX - rect.left;
    this.mouseY = e.clientY - rect.top;
    this.updateGradients();
  };

  private handleMouseLeave = () => {
    this.mouseX = -this.ncGradientSize;
    this.mouseY = -this.ncGradientSize;
    this.updateGradients();
    this.renderer.setStyle(this.borderElement, 'opacity', '0');
    this.renderer.setStyle(this.gradientElement, 'opacity', '0');
  };

  constructor(private el: ElementRef, private renderer: Renderer2) { }

  ngOnInit() {
    this.setupMagicCard();
    this.addEventListeners();
    this.initializeMousePosition();
  }

  private setupMagicCard() {
    const element = this.el.nativeElement;

    const computedStyle = getComputedStyle(element);

    // 设置主容器样式
    const elementPosition = computedStyle.position;
    if (!elementPosition || elementPosition === 'static') {
      this.renderer.setStyle(element, 'position', 'relative');
    }
    this.renderer.addClass(element, 'nc-magic-card');

    // 获取容器的边框宽度
    const borderTopWidth = parseFloat(computedStyle.borderTopWidth) || 0;
    const borderRightWidth = parseFloat(computedStyle.borderRightWidth) || 0;
    const borderBottomWidth = parseFloat(computedStyle.borderBottomWidth) || 0;
    const borderLeftWidth = parseFloat(computedStyle.borderLeftWidth) || 0;

    // 如果没有边框，默认使用1px
    const maxBorderWidth = Math.max(borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth);
    const borderWidth = maxBorderWidth > 0 ? maxBorderWidth : 1;

    // 创建边框元素 - 根据实际边框宽度向外扩展
    this.borderElement = this.renderer.createElement('div');
    this.renderer.addClass(this.borderElement, 'nc-magic-card-border');
    this.renderer.setStyle(this.borderElement, 'pointer-events', 'none');
    this.renderer.setStyle(this.borderElement, 'position', 'absolute');
    this.renderer.setStyle(this.borderElement, 'inset', `-${borderWidth}px`); // 根据边框宽度动态设置
    this.renderer.setStyle(this.borderElement, 'border-radius', 'inherit');
    this.renderer.setStyle(this.borderElement, 'transition', 'opacity 300ms ease');
    this.renderer.setStyle(this.borderElement, 'opacity', '0');
    this.renderer.setStyle(this.borderElement, 'z-index', '1');

    // 创建背景元素 - 隐藏边框层的内部区域
    this.backgroundElement = this.renderer.createElement('div');
    this.renderer.addClass(this.backgroundElement, 'nc-magic-card-background');
    this.renderer.setStyle(this.backgroundElement, 'pointer-events', 'none');
    this.renderer.setStyle(this.backgroundElement, 'position', 'absolute');
    this.renderer.setStyle(this.backgroundElement, 'inset', '0');
    this.renderer.setStyle(this.backgroundElement, 'border-radius', 'inherit');
    this.renderer.setStyle(this.backgroundElement, 'background-color', this.ncCardBackground);
    this.renderer.setStyle(this.backgroundElement, 'z-index', '2');

    // 创建渐变元素
    this.gradientElement = this.renderer.createElement('div');
    this.renderer.addClass(this.gradientElement, 'nc-magic-card-gradient');
    this.renderer.setStyle(this.gradientElement, 'pointer-events', 'none');
    this.renderer.setStyle(this.gradientElement, 'position', 'absolute');
    this.renderer.setStyle(this.gradientElement, 'inset', '0');
    this.renderer.setStyle(this.gradientElement, 'border-radius', 'inherit');
    this.renderer.setStyle(this.gradientElement, 'transition', 'opacity 300ms ease');
    this.renderer.setStyle(this.gradientElement, 'opacity', '0');
    this.renderer.setStyle(this.gradientElement, 'z-index', '4');

    // 原始内容位于背景层之上，渐变层之下
    const children = Array.from(element.children);
    children.forEach((child: any) => {
      const childPosition = getComputedStyle(child).position;
      if (!childPosition || childPosition === 'static') {
        this.renderer.setStyle(child, 'position', 'relative');
      }
      this.renderer.setStyle(child, 'z-index', '3');
    });

    // 添加效果层到原始元素
    this.renderer.appendChild(element, this.borderElement);
    this.renderer.appendChild(element, this.backgroundElement);
    this.renderer.appendChild(element, this.gradientElement);
  }

  private addEventListeners() {
    const element = this.el.nativeElement;
    this.renderer.listen(element, 'mousemove', this.handleMouseMove);
    this.renderer.listen(element, 'mouseleave', this.handleMouseLeave);
    this.renderer.listen(element, 'mouseenter', this.handleMouseEnter);
  }

  private initializeMousePosition() {
    this.mouseX = -this.ncGradientSize;
    this.mouseY = -this.ncGradientSize;
    this.updateGradients();
  }

  private updateGradients() {
    if (this.borderElement && this.gradientElement) {
      // 更新边框渐变
      const borderColors = Array.isArray(this.ncBorderColor) ? this.ncBorderColor.join(',') : this.ncBorderColor;
      const borderGradient = `radial-gradient(${this.ncGradientSize}px circle at ${this.mouseX}px ${this.mouseY}px, ${borderColors}, transparent)`;
      this.renderer.setStyle(this.borderElement, 'background', borderGradient);

      // 更新内部渐变
      const innerColors = Array.isArray(this.ncGradientColor) ? this.ncGradientColor.join(',') : this.ncGradientColor;
      const innerGradient = `radial-gradient(${this.ncGradientSize}px circle at ${this.mouseX}px ${this.mouseY}px, ${innerColors}, transparent 100%)`;
      this.renderer.setStyle(this.gradientElement, 'background', innerGradient);
    }
  }
}